<script setup lang="ts">
import { computed } from 'vue';
import { accentRgb, DEFAULT_ACCENT } from '@/constants/skillTypeAccent';

interface Props {
  text: string;
  /** Frame colour. Skill-typed captions pass the type's accent so the caption
   *  agrees with the cell the player is pointing at. */
  accent?: string;
  hint?: string;
  /** Emphasises a committed selection over an exploratory hover. */
  strong?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  accent: DEFAULT_ACCENT,
  hint: '',
  strong: false
});

// Alpha is varied off one variable rather than eight hard-coded rgba strings, so
// any accent — including a skill type added later — themes the whole frame.
const accentVars = computed(() => ({ '--caption-rgb': accentRgb(props.accent) }));
</script>

<template>
  <div
      class="terminal-caption"
      :class="{ 'terminal-caption--strong': strong }"
      :style="accentVars"
      aria-hidden="true"
  >
    <div class="terminal-caption__line">
      <span class="terminal-caption__prompt">&gt;</span>
      <span class="terminal-caption__text">{{ text }}</span>
      <span class="terminal-caption__caret"></span>
    </div>
    <div v-if="hint" class="terminal-caption__hint">{{ hint }}</div>
  </div>
</template>

<style scoped>
.terminal-caption {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 3px 7px;
  pointer-events: none;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border-radius: 2px;
  /* Text stays near-white whatever the accent: the eight type colours run from
     mid-grey to deep red, and several are unreadable as small text on black. */
  color: #eaf6ff;
  background: rgba(4, 10, 14, 0.92);
  border: 1px solid rgba(var(--caption-rgb), 0.75);
  box-shadow: 0 0 12px rgba(var(--caption-rgb), 0.3), inset 0 0 10px rgba(var(--caption-rgb), 0.08);
  text-shadow: 0 0 6px rgba(var(--caption-rgb), 0.6);
}

.terminal-caption--strong {
  border-width: 2px;
  box-shadow: 0 0 18px rgba(var(--caption-rgb), 0.45), inset 0 0 12px rgba(var(--caption-rgb), 0.12);
}

.terminal-caption__line {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.terminal-caption__prompt {
  color: rgb(var(--caption-rgb));
}

/* The caret holds a fixed slot so the line does not jitter as it types, and
   keeps blinking once the name is complete. */
.terminal-caption__caret {
  width: 6px;
  height: 11px;
  align-self: center;
  background: rgb(var(--caption-rgb));
  box-shadow: 0 0 8px rgba(var(--caption-rgb), 0.9);
  animation: terminal-caption-blink 1s steps(1, end) infinite;
}

/* The hint is secondary and can be a full sentence in some locales, so it wraps
   inside a fixed width instead of stretching the caption across the HUD. */
.terminal-caption__hint {
  font-size: 8px;
  letter-spacing: 0.08em;
  line-height: 1.4;
  max-width: 150px;
  white-space: normal;
  text-align: center;
  color: rgb(var(--caption-rgb));
  opacity: 0.8;
}

@keyframes terminal-caption-blink {
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .terminal-caption__caret {
    animation: none;
  }
}
</style>
